import styled, { keyframes } from "styled-components";

export default function Pricing() {
  const sessions = [
    {
      title: "Couples Session",
      price: "$250",
      label: "Weeknight friendly",
      items: [
        "Includes 1 outfit",
        "1 location (scouted by me if wanted)",
        "Styling tips",
        "30+ photos back digitally",
        "45 minutes to 1 hour",
      ],
    },
    {
      title: "Engagement Session",
      price: "$350",
      label: "Signature",
      items: [
        "Includes 2 outfits",
        "2 locations within 10 minutes of each other",
        "1 to 1.5 hours",
        "50+ photos back digitally",
      ],
    },
    {
      title: "Surprise Proposal",
      price: "$450",
      items: [
        "Includes location scouting",
        "Planning help",
        "50+ photos back digitally",
        "48-hour sneak peek",
        "Optional hiking proposal add-on",
        "Travel fee depending on location",
      ],
    },
    {
      title: "Elopement Option #1 (Small)",
      price: "$800",
      label: "Intimate",
      items: [
        "2–4 people",
        "Planning help & timeline assistance",
        "Styling tips",
        "Location scouting",
        "4 hours",
        "200+ photos back digitally",
        "Polaroid book at end of session",
        "48-hour sneak peek",
        "Travel fee depending on location",
      ],
    },
    {
      title: "Elopement Option #2 (Medium)",
      price: "$1200",
      label: "Full-day",
      items: [
        "2–6 people",
        "Planning help & timeline assistance",
        "Location scouting",
        "6 hours",
        "300+ photos back digitally",
        "48-hour sneak peek",
        "Polaroid book at end of session",
        "Travel fee depending on location",
      ],
    },
    {
      title: "Family Session",
      price: "$350",
      items: [
        "Families of 5 or less",
        "1 location",
        "1 hour session",
        "30+ photos back digitally",
        "Creative sessions optional",
      ],
    },
    {
      title: "Maternity Session",
      price: "$375",
      items: [
        "2 outfits",
        "1 location",
        "1 hour session",
        "50+ photos back digitally",
      ],
    },
  ];

  const addOns = [
    "Polaroid book of session at the end — $45",
    "Extra time — $50 per additional 30 minutes",
    "All shoots include location and styling help if wanted",
  ];

  return (
    <PricingSection>
      <Texture />

      <Header>
        <Eyebrow>Investment</Eyebrow>
        <Title>Collections crafted for storytellers and romantics.</Title>
        <Subtitle>
          Choose the experience that fits your season of life. Every package
          includes gentle direction, location scouting support, and
          heirloom-ready edits.
        </Subtitle>
      </Header>

      <PriceGrid>
        {sessions.map((s, i) => (
          <Card key={i}>
            {s.label && <Badge>{s.label}</Badge>}
            <SessionTitle>{s.title}</SessionTitle>
            <Price>{s.price}</Price>
            <Divider />
            <List>
              {s.items.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </List>
          </Card>
        ))}
      </PriceGrid>

      <AddOns>
        <AddOnsTitle>Optional Add-Ons</AddOnsTitle>
        <AddOnsCopy>
          Layer in extras to personalize your session. We’ll tailor the flow to
          feel natural, unrushed, and true to you.
        </AddOnsCopy>
        <AddOnsList>
          {addOns.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </AddOnsList>
      </AddOns>
    </PricingSection>
  );
}

// 🎨 Styled Components
const shimmer = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const PricingSection = styled.section`
  position: relative;
  background: linear-gradient(135deg, #ada98d 0%, #918c76 45%, #b7b29a 100%);
  color: #1f1d17;
  min-height: 100vh;
  padding: 110px 20px 120px;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
`;

const Texture = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(
      600px circle at 20% 25%,
      rgba(255, 255, 255, 0.15),
      transparent 40%
    ),
    radial-gradient(
      480px circle at 85% 30%,
      rgba(255, 255, 255, 0.12),
      transparent 45%
    ),
    radial-gradient(
      400px circle at 70% 80%,
      rgba(255, 255, 255, 0.1),
      transparent 50%
    );
  pointer-events: none;
  mix-blend-mode: screen;
  opacity: 0.8;
`;

const Header = styled.header`
  text-align: center;
  max-width: 900px;
  margin-bottom: 60px;
  position: relative;
  z-index: 1;
`;

const Eyebrow = styled.p`
  letter-spacing: 0.22em;
  text-transform: uppercase;
  font-size: 0.78rem;
  color: #f6f2e7;
  margin-bottom: 14px;
`;

const Title = styled.h1`
  font-size: clamp(2.1rem, 3.2vw, 2.9rem);
  color: #fefbf4;
  margin-bottom: 18px;
`;

const Subtitle = styled.p`
  color: #f5f1e6;
  opacity: 0.95;
  line-height: 1.6;
`;

const PriceGrid = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 28px;
  max-width: 1100px;
  width: 100%;
`;

const Card = styled.article`
  background: rgba(255, 255, 255, 0.9);
  border-radius: 0px;
  padding: 30px 26px 32px;
  border: 3px solid rgba(69, 71, 47, 0.45);
  box-shadow: 0 12px 38px rgba(0, 0, 0, 0.08);
  position: relative;
  overflow: hidden;
  transition: transform 0.35s ease, box-shadow 0.35s ease;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      120deg,
      rgba(173, 169, 141, 0.12),
      rgba(255, 255, 255, 0)
    );
    opacity: 0;
    transition: opacity 0.35s ease;
  }

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 16px 48px rgba(0, 0, 0, 0.12);

    &::after {
      opacity: 1;
    }
  }
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 12px;
  border-radius: 10px;
  background: linear-gradient(
    120deg,
    rgba(173, 169, 141, 0.22),
    rgba(173, 169, 141, 0.35),
    rgba(173, 169, 141, 0.22)
  );
  color: #3a352a;
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 14px;
  border: 1px solid rgba(173, 169, 141, 0.4);
  background-size: 200% 200%;
  animation: ${shimmer} 8s ease infinite;
`;

const SessionTitle = styled.h2`
  color: #393428;
  font-size: 1.35rem;
  margin-bottom: 6px;
`;

const Price = styled.h3`
  color: #7a735f;
  font-size: 1.15rem;
  letter-spacing: 0.02em;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid rgba(173, 169, 141, 0.35);
  margin: 18px 0 16px;
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  color: #2f2a21;
  display: grid;
  gap: 8px;

  li {
    line-height: 1.5;
    padding-left: 0.5em;
    position: relative;
  }

  li::before {
    content: "•";
    color: #ada98d;
    position: absolute;
    left: -0.5em;
  }
`;

const AddOns = styled.section`
  position: relative;
  z-index: 1;
  margin-top: 80px;
  max-width: 820px;
  text-align: center;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 24px 22px 28px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
`;

const AddOnsTitle = styled.h3`
  color: #fefbf4;
  margin-bottom: 10px;
  font-size: 1.3rem;
`;

const AddOnsCopy = styled.p`
  color: #f5f1e6;
  opacity: 0.95;
  line-height: 1.6;
  margin-bottom: 14px;
`;

const AddOnsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  color: #fefbf4;
  display: grid;
  gap: 8px;

  li {
    padding: 10px 12px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.18);
  }
`;
